'use client'

import type { ColumnFiltersState, OnChangeFn, SortingState } from '@tanstack/react-table'
import type { ReactNode } from 'react'
import { useState } from 'react'
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'

import { TableBody, TableCell, Table as TableComponent, TableHead, TableHeader, TableRow } from '@ui/web/components'
import { typographyVariants } from '@ui/web/lib'
import cn from '@config/tailwind/cn'

import type { RenderStudentName } from './columns'
import type { Student, StudentAction, StudentColumnId, StudentFilterId } from './types'
import { useStudentColumns } from './columns'
import { StudentDialogs } from './dialogs'
import { Toolbar } from './toolbar'
import { DEFAULT_STUDENT_COLUMN_IDS, STUDENT_FILTER_IDS } from './types'

export interface StudentTableProps {
  data: Student[]
  /** Drives copy that differs between a school roster and a family's own children. */
  type?: 'web' | 'school'
  /** False for a `member`-role viewer; inline editors and destructive items are disabled, not hidden. */
  canEdit: boolean
  /**
   * Governs the inline Kelas editor alone. Defaults to `canEdit`. That cell writes `setChildTeams`,
   * which stays owner/admin, so a roster that lets a guru kelas edit their students still has to
   * keep the class column read-only for them.
   */
  canEditClass?: boolean
  /**
   * `menu` (default) is the kebab: account settings, premium seat, remove from school. `settings`
   * is a single edit button opening the account sheet — for rosters where the school-level verbs
   * belong elsewhere. Ignored when `renderRowActions` is given.
   */
  rowAction?: 'menu' | 'settings'
  /** `menu` (default) offers the WhatsApp message too; `copy` is a one-click PIN copy. */
  pinAction?: 'menu' | 'copy'
  /** Rendered inside the table's own frame, directly under the last row. */
  footer?: ReactNode

  /**
   * Which data columns to render, in order. Defaults to the full set. Drop `schoolClass` on a
   * roster already scoped to one class — and remember its data only exists when the query was made
   * with `includeAllSchoolClasses`.
   */
  columns?: StudentColumnId[]
  /** Which toolbar selects to render. Defaults to the full set; pass `[]` for search only. */
  filters?: StudentFilterId[]
  searchPlaceholder?: string

  /**
   * `card` is the standalone, width-capped roster (rounded border, no padding of its own).
   * `bleed` is the full-width roster inside a tabbed page shell: horizontal rules top and bottom,
   * the table edge-to-edge, and the toolbar inset to match the page's own gutter.
   */
  appearance?: 'card' | 'bleed'

  /** Checkbox column plus, once anything is ticked, whatever `renderBulkActions` returns. */
  enableRowSelection?: boolean
  renderBulkActions?: (args: { selected: Student[]; deselect: () => void }) => ReactNode

  renderStudentName?: RenderStudentName
  /**
   * Right-hand action cell. Omit for the default kebab (account settings / premium seat / remove
   * from school) and its dialogs.
   */
  renderRowActions?: (student: Student) => ReactNode

  /**
   * Lifts filter state out of the table. Pass both to own it — Kelola murid does, so that the class
   * and grade filters can live in `?kelas=` / `?tingkat=` and be shared as a link. Left undefined,
   * the table keeps its own filter state.
   */
  columnFilters?: ColumnFiltersState
  onColumnFiltersChange?: OnChangeFn<ColumnFiltersState>
}

/**
 * The school's student roster, in one table.
 *
 * Everything a roster does to a student — editing the class or grade inline, copying a PIN, handing
 * out a premium seat, removing the account — lives here, so the pages that show students differ
 * only in which columns they ask for and what their row button does.
 */
export function StudentTable({
  data,
  type,
  canEdit,
  canEditClass,
  rowAction,
  pinAction,
  footer,
  columns: columnIds = [...DEFAULT_STUDENT_COLUMN_IDS],
  filters = [...STUDENT_FILTER_IDS],
  searchPlaceholder = 'Cari nama',
  appearance = 'card',
  enableRowSelection = false,
  renderBulkActions,
  renderStudentName,
  renderRowActions,
  columnFilters: controlledColumnFilters,
  onColumnFiltersChange,
}: StudentTableProps) {
  const [action, setAction] = useState<StudentAction | null>(null)

  const columns = useStudentColumns({
    type,
    canEdit,
    canEditClass,
    rowAction,
    pinAction,
    columnIds,
    enableRowSelection,
    renderStudentName,
    renderRowActions,
    onAction: setAction,
  })

  const [sorting, setSorting] = useState<SortingState>([])
  const [localColumnFilters, setLocalColumnFilters] = useState<ColumnFiltersState>([])
  const [rowSelection, setRowSelection] = useState({})

  const isFilterControlled = controlledColumnFilters !== undefined && onColumnFiltersChange !== undefined
  const columnFilters = isFilterControlled ? controlledColumnFilters : localColumnFilters

  const table = useReactTable({
    data,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: isFilterControlled ? onColumnFiltersChange : setLocalColumnFilters,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    // Selection is keyed by student id, so ticking a row survives a re-sort or a refetch instead of
    // following whatever now sits at that row index.
    getRowId: (row) => row.id,
    enableRowSelection,
    state: {
      sorting,
      columnFilters,
      rowSelection,
    },
  })

  const selected = enableRowSelection ? table.getFilteredSelectedRowModel().rows.map((row) => row.original) : []

  const bulkActions =
    enableRowSelection && renderBulkActions && selected.length > 0
      ? renderBulkActions({ selected, deselect: () => table.resetRowSelection() })
      : null

  const isBleed = appearance === 'bleed'

  return (
    <div className={cn('srd:flex srd:w-full srd:flex-col srd:gap-y-4', isBleed && 'srd:min-h-[50dvh] srd:pt-4')}>
      <div className={cn(isBleed && 'srd:px-4 srd:sm:px-8')}>
        <Toolbar table={table} filters={filters} searchPlaceholder={searchPlaceholder} actions={bulkActions} />
      </div>

      <div
        className={cn(
          'srd:border-outline srd:bg-neutral-white srd:text-neutral-black srd:overflow-hidden',
          isBleed ? 'srd:border-y' : 'srd:rounded-xl srd:border',
        )}
      >
        <TableComponent>
          <TableHeader className='srd:bg-grey-10'>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id} className='srd:font-medium'>
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>

          <TableBody>
            {table.getRowModel().rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} className='srd:h-24 srd:text-center'>
                  <p className={cn(typographyVariants({ variant: 'body-md' }), 'srd:text-grey-100')}>
                    Tidak ada hasil yang ditemukan
                  </p>
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id} data-state={row.getIsSelected() && 'selected'}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </TableComponent>

        {footer}
      </div>

      <StudentDialogs action={action} type={type} onAction={setAction} onClose={() => setAction(null)} />
    </div>
  )
}
